/** Подавление браузерных артефактов на телефоне + полноэкранный режим.
 *
 *  Что мешало играть: при стрельбе и свайпах вылезало выделение текста,
 *  пинч-зум, лупа по долгому тапу и pull-to-refresh. Часть лечится CSS
 *  (см. index.html), но НЕ всё:
 *   - iOS игнорирует `user-scalable=no` начиная с iOS 10 — пинч-зум ловится
 *     только перехватом нестандартных событий gesturestart/gesturechange;
 *   - двойной тап на iOS зумит даже при touch-action: manipulation, если
 *     между тапами < 300 мс на не-интерактивном элементе;
 *   - контекстное меню по долгому тапу приходит отдельным событием.
 *
 *  Полный экран: на Android есть настоящий Fullscreen API и лок ориентации.
 *  На iPhone его НЕТ (Apple поддерживает fullscreen только на iPad), поэтому
 *  единственный путь к игре без интерфейса браузера — запуск с домашнего
 *  экрана как PWA (manifest.json + apple-mobile-web-app-capable).
 */

/** Запущено ли из «На экран Домой» / установленного PWA. */
export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    window.matchMedia?.("(display-mode: fullscreen)").matches === true ||
    nav.standalone === true
  );
}

export function isIOS(): boolean {
  const ua = navigator.userAgent;
  // iPadOS 13+ отдаёт десктопный UA — отличаем по тач-точкам.
  return /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

/** Fullscreen API есть? На iPhone — нет (на iPad и Android — да). */
export function canRequestFullscreen(): boolean {
  const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> };
  return typeof el.requestFullscreen === "function" || typeof el.webkitRequestFullscreen === "function";
}

/** Вызывать ТОЛЬКО из обработчика реального жеста — иначе браузер откажет. */
export async function enterFullscreen(): Promise<void> {
  const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> };
  try {
    if (document.fullscreenElement) return;
    if (typeof el.requestFullscreen === "function") await el.requestFullscreen({ navigationUI: "hide" });
    else if (typeof el.webkitRequestFullscreen === "function") await el.webkitRequestFullscreen();
  } catch { /* пользователь или политика отказали — не критично */ }
  try {
    const orient = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
    await orient?.lock?.("landscape");
  } catch { /* лок ориентации не везде разрешён */ }
}

/** Перехваты, которые нельзя сделать через CSS. Идемпотентно. */
let installed = false;
export function installMobileGestureGuards(): void {
  if (installed) return;
  installed = true;

  // iOS pinch-zoom: нестандартные, но единственный способ его погасить.
  for (const type of ["gesturestart", "gesturechange", "gestureend"]) {
    document.addEventListener(type, (e) => e.preventDefault(), { passive: false });
  }

  // Долгий тап → контекстное меню / «Скопировать». В полях ввода оставляем.
  document.addEventListener("contextmenu", (e) => {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
    e.preventDefault();
  });

  // Двойной тап → зум (iOS игнорирует touch-action на не-интерактивных узлах).
  let lastTouchEnd = 0;
  document.addEventListener("touchend", (e) => {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "BUTTON" ||
              t.tagName === "SELECT" || t.tagName === "A" || t.closest("button,a,input,select,textarea"))) {
      lastTouchEnd = 0;
      return;
    }
    const now = Date.now();
    if (now - lastTouchEnd <= 300) e.preventDefault();
    lastTouchEnd = now;
  }, { passive: false });

  // Мультитач-свайп по игровому полю не должен скроллить страницу.
  document.addEventListener("touchmove", (e) => {
    if (e.touches.length > 1) e.preventDefault();
  }, { passive: false });
}
